import { FilesStore } from "@/common/FilesUploader";
import {
    Box,
    Button,
    CircularProgress,
    Typography,
    useTheme,
    Fab,
    useScrollTrigger,
    Slide,
} from "@mui/material";
import { useState, useRef, useEffect, useCallback } from "react";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { error400Handler, error422Handler, request } from "@/Api";

import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setError } from "@/nav/navSlice";
import { FloatingBottomContainer } from "@/sheet/BidSum";
import InvoiceItemCard from "./components/InvoiceItemCard";

const CAN_SUBMIT_STATUSES = ["error", "success"];

const calculateMergedBillLines = (billLines) => {
    if (!billLines || billLines.length === 0) return [];
    const name = billLines[0].name || "";
    const docket = billLines[0].docket || "";
    const amount = billLines.reduce(
        (sum, line) => sum + parseFloat(line.amount || 0),
        0,
    );
    const tax_amount = billLines.reduce(
        (sum, line) => sum + parseFloat(line.tax_amount || line.tax || 0),
        0,
    );
    return [{ ...billLines[0], name, docket, amount, tax: tax_amount }];
};

const setOcrResponseProjectDocket = (invoiceData, projectDocket = null) => {
    if (!invoiceData.items || !projectDocket) return invoiceData;
    const updatedItems = invoiceData.items.map((item) => ({
        ...item,
        docket: projectDocket,
    }));
    return { ...invoiceData, items: updatedItems };
};

function ScrollTop(props) {
    const { children, target } = props;
    const trigger = useScrollTrigger({
        target: target || window,
        disableHysteresis: true,
        threshold: 300,
    });

    const handleClick = () => {
        if (target && target !== window) {
            target.scrollTo({ top: 0, behavior: "smooth" });
        } else {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    return (
        <Box sx={{ position: "sticky", bottom: 100, width: "100%", height: 0 }}>
            <Slide direction="up" in={trigger}>
                <Box
                    onClick={handleClick}
                    role="presentation"
                    sx={{ position: "absolute", right: 32, bottom: 0 }}
                >
                    {children}
                </Box>
            </Slide>
        </Box>
    );
}

export default function InvoiceFormMulti({
    publicId,
    onSuccess,
    projectDocket = null,
}) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const [invoices, setInvoices] = useState([]);
    const [waiting, setWaiting] = useState(false);

    const scrollContainerRef = useRef(null);
    const [scrollTarget, setScrollTarget] = useState(undefined);

    useEffect(() => {
        // Find closest scrollable ancestor on mount
        let node = scrollContainerRef.current;
        while (
            node &&
            node !== document.body &&
            node !== document.documentElement
        ) {
            const style = window.getComputedStyle(node);
            if (style.overflowY === "auto" || style.overflowY === "scroll") {
                setScrollTarget(node);
                return;
            }
            node = node.parentElement;
        }
        setScrollTarget(window);
    }, []);

    const canSubmit =
        invoices.every((inv) => CAN_SUBMIT_STATUSES.includes(inv.status)) &&
        invoices.length > 0;

    const handleFilesTransfer = async (newFiles) => {
        const newInvoiceObjects = newFiles.map((file) => ({
            file: file,
            isPdf: file.type === "application/pdf",
            fileUrl: URL.createObjectURL(file),
            status: "loading",
            error: null,
            items: [],
            notes: "",
            splitByLineItems: false,
            isCollapsed: false,
        }));

        const startIndex = invoices.length;
        setInvoices((prev) => [...prev, ...newInvoiceObjects]);

        // Auto-scroll to the top of the first newly added bill
        setTimeout(() => {
            const newElement = document.getElementById(
                `invoice-container-${startIndex}`,
            );
            if (newElement) {
                newElement.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            } else {
                if (scrollTarget && scrollTarget !== window) {
                    scrollTarget.scrollTo({
                        top: scrollTarget.scrollHeight,
                        behavior: "smooth",
                    });
                } else {
                    window.scrollTo({
                        top: document.body.scrollHeight,
                        behavior: "smooth",
                    });
                }
            }
        }, 100);

        for (const inv of newInvoiceObjects) {
            await postOcrRequest(inv.file);
        }
    };

    const handleDeleteInvoice = useCallback((indexToDelete) => {
        setInvoices((prev) => prev.filter((_, i) => i !== indexToDelete));
    }, []);

    const updateInvoice = useCallback((index, field, value) => {
        setInvoices((prev) => {
            const copy = [...prev];
            copy[index] = { ...copy[index], [field]: value };
            return copy;
        });
    }, []);

    const postOcrRequest = async (fileObj) => {
        const formData = new FormData();
        formData.append("file", fileObj, fileObj.name);

        try {
            const res = await request.post("/accounting-bills/ocr", formData);
            const invoiceData = setOcrResponseProjectDocket(
                res.data,
                projectDocket,
            );
            invoiceData.itemsMerged = calculateMergedBillLines(
                invoiceData.items || [],
            );
            invoiceData.itemsSplitted = invoiceData.items || [];
            invoiceData.items = invoiceData.itemsMerged;
            setInvoices((prev) =>
                prev.map((inv) =>
                    inv.file === fileObj
                        ? { ...inv, status: "success", ...invoiceData }
                        : inv,
                ),
            );
        } catch (err) {
            setInvoices((prev) =>
                prev.map((inv) =>
                    inv.file === fileObj
                        ? {
                              ...inv,
                              status: "error",
                              error: `OCR Failed: ${err.toString()}`,
                          }
                        : inv,
                ),
            );
        }
    };

    const submitInvoices = () => {
        setWaiting(true);

        const formData = new FormData();

        const files = invoices.map((inv) => inv.file);
        const invoicesData = JSON.stringify(invoices);
        formData.append("accounting_bills", invoicesData);
        files.forEach((file) => {
            formData.append("files", file, file.name);
        });

        request
            .post("/accounting-bills/public", formData, {
                headers: { "Content-Type": "multipart/form-data" },
                params: { public_id: publicId },
            })
            .then((_) => {
                if (onSuccess) {
                    onSuccess();
                    return;
                }
                navigate("/ap/invoice-thank-you");
            })
            .catch((error) => {
                let hasPerBillError = false;

                if (
                    error.response?.status === 422 &&
                    Array.isArray(error.response?.data?.detail)
                ) {
                    const detail = error.response.data.detail;
                    const errorMap = {};
                    detail.forEach((err) => {
                        const idx = err.index;
                        if (idx !== undefined) {
                            if (!errorMap[idx]) errorMap[idx] = [];
                            let fieldName = "";
                            if (err.loc && err.loc.length > 0) {
                                const lastLoc = err.loc[err.loc.length - 1];
                                if (lastLoc !== "__root__") {
                                    fieldName = `${lastLoc}: `;
                                }
                            }
                            errorMap[idx].push(`${fieldName}${err.msg}`);
                            hasPerBillError = true;
                        }
                    });

                    if (hasPerBillError) {
                        setInvoices((prev) =>
                            prev.map((inv, idx) =>
                                errorMap[idx]
                                    ? {
                                          ...inv,
                                          status: "error",
                                          error: errorMap[idx].join(", "),
                                      }
                                    : {
                                          ...inv,
                                          status: "success",
                                          error: null,
                                      },
                            ),
                        );

                        // Scroll to the first error logically
                        const firstErrorIdx =
                            detail.find((err) => err.index !== undefined)
                                ?.index || 0;
                        const errorElement = document.getElementById(
                            `invoice-container-${firstErrorIdx}`,
                        );
                        if (errorElement) {
                            errorElement.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                            });

                            // Also scroll the right panel to the top so the specific error text is visible
                            const rightPanel = document.getElementById(
                                `invoice-form-${firstErrorIdx}`,
                            );
                            if (rightPanel) {
                                rightPanel.scrollTo({
                                    top: 0,
                                    behavior: "smooth",
                                });
                            }
                        }

                        dispatch(
                            setError({
                                message:
                                    "Some bills have validation errors. Please review them.",
                            }),
                        );
                        return;
                    }
                }

                let errorMessage =
                    error.response?.data?.message || "Submission Failed";
                error422Handler(error, (parsedError) => {
                    errorMessage = JSON.stringify(parsedError);
                });
                error400Handler(error, (parsedError) => {
                    errorMessage = JSON.stringify(parsedError);
                });
                dispatch(setError({ message: errorMessage }));
            })
            .finally(() => setWaiting(false));
    };

    return (
        <Box ref={scrollContainerRef} sx={{ width: "100%", mt: 2 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                    Add bills
                </Typography>
                <Typography variant="subtitle2" sx={{ mb: 3, color: "#aaa" }}>
                    Manually upload bills to any of your projects. Drag and drop
                    as many documents as you want.
                </Typography>
                <FilesStore
                    acceptedFormats={[".pdf", ".png", ".jpg"]}
                    innerTitle={"upload multiple invoices!"}
                    onFilesTransfer={handleFilesTransfer}
                />
            </Box>

            {/* Stacked Invoice Containers */}
            {invoices.map((inv, idx) => (
                <InvoiceItemCard
                    key={idx}
                    invoice={inv}
                    index={idx}
                    updateInvoice={updateInvoice}
                    handleDeleteInvoice={handleDeleteInvoice}
                    projectDocket={projectDocket}
                />
            ))}

            {scrollTarget && (
                <ScrollTop target={scrollTarget}>
                    <Fab
                        color="primary"
                        size="small"
                        aria-label="scroll back to top"
                    >
                        <KeyboardArrowUpIcon />
                    </Fab>
                </ScrollTop>
            )}

            {/* Spacer ensures last container clears the floating bar */}
            {invoices.length > 0 && <Box sx={{ height: 120 }} />}

            {/* Bottom sticky submission bar */}
            {invoices.length > 0 && (
                <FloatingBottomContainer
                    theme={theme}
                    style={{
                        justifyContent: "flex-end",
                        position: "sticky",
                        bottom: "10px",
                        width: "100%",
                        boxSizing: "border-box",
                        marginLeft: 0,
                        marginRight: 0,
                    }}
                >
                    <Typography
                        sx={{ mr: 3, color: "text.secondary", fontWeight: 500 }}
                    >
                        {invoices.length} bill{invoices.length > 1 ? "s" : ""}{" "}
                        uploaded
                    </Typography>
                    {waiting ? (
                        <CircularProgress size={24} sx={{ mr: 2 }} />
                    ) : (
                        <Button
                            onClick={submitInvoices}
                            variant="contained"
                            disabled={!canSubmit}
                        >
                            Add Bills
                        </Button>
                    )}
                </FloatingBottomContainer>
            )}
        </Box>
    );
}
